import { useEffect, useState } from "react";
import { http, apiError } from "@/lib/api";
import { EnvelopeSimple, PaperPlaneTilt, Clock, Warning, Check } from "@phosphor-icons/react";

export default function EmailPanel() {
  const [log, setLog] = useState([]);
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");

  const refresh = async () => {
    try {
      const { data } = await http.get("/coach/emails/log?limit=10");
      setLog(data || []);
    } catch (e) {
      setErr(apiError(e));
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const trigger = async (kind) => {
    setBusy(kind);
    setMsg("");
    setErr("");
    try {
      const { data } = await http.post("/coach/emails/trigger", { kind });
      const summary =
        kind === "daily"
          ? `Daily run · sent ${data.sent} · skipped ${data.skipped}`
          : `Weekly digest sent (${data.clients} clients summarised)`;
      setMsg(summary);
      await refresh();
    } catch (e) {
      setErr(apiError(e));
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="id-card" data-testid="coach-email-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <EnvelopeSimple size={16} weight="bold" className="text-[#FF3333]" />
          <div className="id-label">Email Reminders</div>
        </div>
        <div className="text-[10px] text-zinc-500 font-manrope uppercase tracking-[0.15em]">
          Daily 7am AEST · Sunday 6pm AEST
        </div>
      </div>

      <div className="flex gap-2 mb-4">
        <button
          type="button"
          onClick={() => trigger("daily")}
          disabled={!!busy}
          className="flex-1 border border-[#262626] hover:border-[#FF3333] disabled:opacity-40 disabled:cursor-not-allowed bg-[#0F0F0F] hover:bg-[#1A1A1A] transition-colors px-4 py-3 font-oswald uppercase text-sm tracking-[0.15em] flex items-center justify-center gap-2"
          data-testid="email-trigger-daily"
        >
          <PaperPlaneTilt size={14} weight="bold" />
          {busy === "daily" ? "Sending..." : "Test Daily Reminder"}
        </button>
        <button
          type="button"
          onClick={() => trigger("weekly")}
          disabled={!!busy}
          className="flex-1 border border-[#262626] hover:border-[#FF3333] disabled:opacity-40 disabled:cursor-not-allowed bg-[#0F0F0F] hover:bg-[#1A1A1A] transition-colors px-4 py-3 font-oswald uppercase text-sm tracking-[0.15em] flex items-center justify-center gap-2"
          data-testid="email-trigger-weekly"
        >
          <PaperPlaneTilt size={14} weight="bold" />
          {busy === "weekly" ? "Sending..." : "Test Weekly Digest"}
        </button>
      </div>

      {msg && (
        <div className="border border-[#FF3333] bg-[#FF3333]/5 px-3 py-2 mb-3 text-xs text-zinc-200 font-manrope flex items-center gap-2" data-testid="email-msg">
          <Check size={14} weight="bold" className="text-[#FF3333]" /> {msg}
        </div>
      )}
      {err && (
        <div className="border border-[#FF3333] bg-[#FF3333]/10 px-3 py-2 mb-3 text-xs text-[#FF3333] font-manrope" data-testid="email-err">
          {err}
        </div>
      )}

      <div className="border-t border-[#262626] pt-4">
        <div className="id-label mb-3 text-zinc-500">Recent activity</div>
        {log.length === 0 ? (
          <div className="text-zinc-600 text-xs font-manrope">No emails sent yet.</div>
        ) : (
          <div className="space-y-1.5" data-testid="email-log">
            {log.map((e, i) => (
              <div key={i} className="flex items-start gap-2 text-xs font-manrope">
                {e.status === "sent" ? (
                  <Check size={12} weight="bold" className="text-emerald-500 mt-0.5 shrink-0" />
                ) : e.status === "failed" ? (
                  <Warning size={12} weight="bold" className="text-[#FF3333] mt-0.5 shrink-0" />
                ) : (
                  <Clock size={12} weight="bold" className="text-zinc-500 mt-0.5 shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-zinc-300 truncate">{e.subject}</div>
                  <div className="text-zinc-600 text-[10px]">
                    → {e.to} · {e.kind} · {new Date(e.sent_at).toLocaleString()}
                  </div>
                  {e.error && (
                    <div className="text-[#FF3333] text-[10px] mt-0.5 line-clamp-2">{e.error}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
